import React, { Suspense, lazy, useState } from 'react';
import { Tooltip } from './ui/Tooltip';
import { config } from '../config/config';

// Driver moves drawer (the same one At Risk Monitor opens from a driver number).
// lazy-loaded so the driver-status-monitor lane/move-card bundle stays out of
// every grid that renders a driver value until someone actually opens it.
const DriverLoadsDrawerV2 = lazy(() => import('./DriverLoadsDrawerV2'));

interface DriverIconLinksProps {
  /** Driver unit number. Callers are responsible for only rendering this for a
   *  real GWI unit — blank, the '000' bypass unit, and outside-carrier
   *  pseudo-drivers have neither a safety record nor moves to show. */
  unitNumber: string;
  /** Resolved driver name, when the caller already has it. Used for the
   *  tooltips and to seed the drawer header; the drawer resolves its own name
   *  from the API when omitted. */
  driverName?: string;
  /** PRO the icons are rendered next to (e.g. the Pro drawer's PRO) — highlighted
   *  in the moves drawer so the user can spot the PRO they came from. */
  highlightPronumber?: number;
  /** Whether to offer the moves icon at all. The drawer only ever shows TODAY's
   *  moves, so callers pass `false` for a leg outside that window (see
   *  `legIsInTodaysMoves`) rather than link to an unrelated day's work.
   *  Defaults to `true` for callers with no leg-date context. */
  showMoves?: boolean;
  className?: string;
}

/**
 * The pair of driver icons shown next to an assigned driver:
 *
 *   1. Silhouette → opens the V1 Safety System for that unit in a new tab.
 *   2. List       → opens the driver moves drawer (all of today's moves).
 *                   Omitted when `showMoves` is false — the drawer is scoped to
 *                   today, so it has nothing to show for a leg scheduled on
 *                   another day. The silhouette is not date-scoped and stays.
 *
 * Purely presentational + self-contained: shared by the editable
 * DriverSelectorCell and the read-only driver displays in the Pro drawer's
 * legs grid, so both render the same affordances.
 */
export const DriverIconLinks: React.FC<DriverIconLinksProps> = ({
  unitNumber,
  driverName,
  highlightPronumber,
  showMoves = true,
  className = '',
}) => {
  const [movesDrawerOpen, setMovesDrawerOpen] = useState(false);

  const unit = unitNumber.trim();
  const label = driverName || unit;

  // testcms → testsafetynew (test), cms → safetynew (prod)
  const safetyUrl = `${config.LEGACY_CMS_URL.replace('cms', 'safetynew')}/index_safety.pl?action=savecustomsearch&searchtype=unitnumber&searchresulttype=ALL&searchdisplay=driver&contlist=${encodeURIComponent(unit)}`;

  const iconClass =
    'w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors cursor-pointer';

  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Tooltip content={`View ${label} in Safety System`} placement="top">
        <a
          href={safetyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0"
          aria-label={`View driver ${label} in Safety System`}
          onClick={(e) => e.stopPropagation()}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={iconClass} fill="currentColor">
            <circle cx="12" cy="6" r="6" />
            <path d="M16.043,14H7.957A4.963,4.963,0,0,0,3,18.957V24H21V18.957A4.963,4.963,0,0,0,16.043,14Z" />
          </svg>
        </a>
      </Tooltip>

      {showMoves && (
        <Tooltip content={`View moves for ${label}`} placement="top">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMovesDrawerOpen(true);
            }}
            className="inline-flex shrink-0"
            aria-label={`View moves for driver ${label}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className={iconClass} fill="currentColor">
              <path d="m19,1H5C2.243,1,0,3.243,0,6v12c0,2.757,2.243,5,5,5h14c2.757,0,5-2.243,5-5V6c0-2.757-2.243-5-5-5Zm3,17c0,1.654-1.346,3-3,3H5c-1.654,0-3-1.346-3-3V6c0-1.654,1.346-3,3-3h14c1.654,0,3,1.346,3,3v12Zm-3-11c0,.552-.448,1-1,1h-7c-.552,0-1-.448-1-1s.448-1,1-1h7c.552,0,1,.448,1,1Zm-11,0c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Zm11,5c0,.552-.448,1-1,1h-7c-.552,0-1-.448-1-1s.448-1,1-1h7c.552,0,1,.448,1,1Zm-11,0c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Zm11,5c0,.552-.448,1-1,1h-7c-.552,0-1-.448-1-1s.448-1,1-1h7c.552,0,1,.448,1,1Zm-11,0c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Z" />
            </svg>
          </button>
        </Tooltip>
      )}

      {/* Only mounted once opened (lazy chunk) */}
      {showMoves && movesDrawerOpen && (
        <Suspense fallback={null}>
          <DriverLoadsDrawerV2
            isOpen={movesDrawerOpen}
            onClose={() => setMovesDrawerOpen(false)}
            unitNumber={unit}
            driverName={driverName || undefined}
            highlightPronumber={highlightPronumber}
          />
        </Suspense>
      )}
    </span>
  );
};

export default DriverIconLinks;
